import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, API_VERSION } from '../../../lib/apiUrl';
import type { WebSearchStatusEvent } from '../types';

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 3_000;
const MAX_RECONNECT_DELAY = 15_000;

interface UseWebSearchStreamOptions {
  organizationId: string;
  enabled?: boolean;
  onEvent?: (event: WebSearchStatusEvent) => void;
  getAccessToken?: () => Promise<string | null>;
}

interface UseWebSearchStreamReturn {
  isConnected: boolean;
  lastEvent: WebSearchStatusEvent | null;
}

export function useWebSearchStream({
  organizationId,
  enabled = false,
  onEvent,
  getAccessToken,
}: UseWebSearchStreamOptions): UseWebSearchStreamReturn {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WebSearchStatusEvent | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);
  const getAccessTokenRef = useRef(getAccessToken);
  const connectIdRef = useRef(0);

  onEventRef.current = onEvent;
  getAccessTokenRef.current = getAccessToken;

  const cleanup = useCallback(() => {
    connectIdRef.current += 1;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(async () => {
    if (!organizationId || !enabled) return;

    cleanup();

    const myConnectId = connectIdRef.current;

    if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    let tokenParam = '';
    if (getAccessTokenRef.current) {
      try {
        const token = await getAccessTokenRef.current();
        if (token) {
          tokenParam = `&token=${encodeURIComponent(token)}`;
        } else {
          return;
        }
      } catch {
        return;
      }
    }

    if (myConnectId !== connectIdRef.current) return;

    const url = `${API_URL}/api/${API_VERSION}/web-search/status/stream?organizationId=${organizationId}${tokenParam}`;
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      if (myConnectId !== connectIdRef.current) {
        eventSource.close();
        return;
      }
      setIsConnected(true);
      reconnectAttemptRef.current = 0;
    };

    eventSource.onmessage = (e) => {
      if (myConnectId !== connectIdRef.current) return;
      try {
        const parsed: WebSearchStatusEvent = JSON.parse(e.data);
        setLastEvent(parsed);
        onEventRef.current?.(parsed);

        if (
          parsed.status === 'completed' ||
          parsed.status === 'failed' ||
          parsed.status === 'no_results' ||
          parsed.status === 'cancelled'
        ) {
          queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          queryClient.invalidateQueries({ queryKey: ['web-search-results'] });
        }
      } catch {
        // skip malformed events
      }
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }

      if (myConnectId !== connectIdRef.current) return;

      reconnectAttemptRef.current += 1;

      if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }

      const delay = Math.min(
        BASE_RECONNECT_DELAY * 2 ** reconnectAttemptRef.current,
        MAX_RECONNECT_DELAY,
      );

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
  }, [organizationId, enabled, cleanup, queryClient]);

  useEffect(() => {
    reconnectAttemptRef.current = 0;
    connect();
    return cleanup;
  }, [connect, cleanup]);

  return { isConnected, lastEvent };
}
